import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '../supabase';
import { logAuthEvent } from '../utils/sessionAudit';
import { desactivarAvisosAdminEsteDispositivo } from '../utils/pushSubscription';

const AuthContext = createContext({});
const PROFILE_REFRESH_EVENTS = new Set(['INITIAL_SESSION', 'USER_UPDATED']);
const PROFILE_FETCH_RETRY_MS = [250, 600, 1200];

// Traemos profile + organization en UN solo request usando el embed de
// PostgREST (profiles.organization_id -> organizations). Antes eran dos
// roundtrips en serie, y en mobile esa segunda ida y vuelta se siente: cae
// justo mientras ProtectedRoute muestra el spinner.
//
// Gastify (downstream) todavia no tiene declarada la FK, y sin FK PostgREST
// rechaza el embed con PGRST200. Por eso probamos una vez y, si el backend no
// lo soporta, caemos al camino clasico de dos queries por el resto de la
// sesion. Mismo codigo, los dos esquemas.
let profileEmbedSupported = true;

const isMissingRelationshipError = (error) => {
  if (!error) return false;
  // PGRST200: no existe la relacion. PGRST201: hay mas de una y es ambigua.
  // En los dos casos el embed no sirve y hay que ir por el camino de dos queries.
  if (error.code === 'PGRST200' || error.code === 'PGRST201') return true;
  return /could not find a relationship|more than one relationship|relationship between/i.test(
    String(error?.message || '')
  );
};

// Detecta el error específico de refresh token inválido / no encontrado.
// Cuando aparece, los tokens en localStorage están podridos y no sirve seguir
// intentando: hay que limpiarlos para que el usuario entre con un login fresco.
const isRefreshTokenError = (error) => {
  if (!error) return false;
  const message = String(error?.message || error || '').toLowerCase();
  return (
    message.includes('refresh token not found') ||
    message.includes('invalid refresh token') ||
    error?.code === 'refresh_token_not_found'
  );
};

// Borra cualquier llave de Supabase persistida en localStorage. Útil cuando el
// refresh token quedó inválido: deja al usuario con sesión limpia.
const clearStaleSupabaseAuth = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const keysToRemove = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && (key.startsWith('sb-') || key.startsWith('supabase.'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => window.localStorage.removeItem(key));
  } catch (e) {
    console.warn('No se pudieron limpiar tokens viejos de Supabase:', e);
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const profileRef = useRef(null);
  const organizationIdRef = useRef(null);
  // Userid con un fetchProfile en curso. Dedup de llamadas concurrentes para
  // evitar la doble carga de profile+organization en el arranque (carrera
  // getSession vs onAuthStateChange) que disparaba el N+1 de organizations
  // (ref IMPULZA-4).
  const inFlightProfileUserRef = useRef(null);

  useEffect(() => {
    profileRef.current = profile;
  }, [profile]);

  useEffect(() => {
    organizationIdRef.current = organization?.id || null;
  }, [organization?.id]);

  // Carga la row completa de organizations una sola vez por sesión. Las páginas
  // consumen `organization` desde el context en lugar de hacer su propio fetch.
  // Antes había 12+ lugares haciendo `from('organizations').select('*')` y eso
  // disparaba el N+1 detector de Sentry (ref IMPULZA-4).
  const fetchOrganization = async (orgId) => {
    if (!orgId) {
      setOrganization(null);
      return;
    }
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', orgId)
      .maybeSingle();

    if (error) {
      console.error('No pudimos cargar la organización.', error);
      return;
    }
    setOrganization(data || null);
  };

  useEffect(() => {
    // 1. Verificar sesión actual.
    //    Si el refresh token está vencido / revocado, Supabase tira 400. Lo capturamos,
    //    limpiamos los tokens podridos del localStorage y dejamos al usuario sin sesión
    //    para que vea la pantalla de login en lugar de spam en consola.
    supabase.auth
      .getSession()
      .then(({ data: { session }, error }) => {
        if (error && isRefreshTokenError(error)) {
          clearStaleSupabaseAuth();
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }

        setUser((current) => {
          const nextUser = session?.user ?? null;
          if (current?.id === nextUser?.id) return current;
          return nextUser;
        });
        if (session?.user) {
          // Identificación temprana — el profile completo se setea en
          // fetchProfile, pero mientras tanto Sentry ya sabe quién es.
          Sentry.setUser({ id: session.user.id, email: session.user.email });
          fetchProfile(session.user.id);
        } else {
          setLoading(false);
        }
      })
      .catch((err) => {
        if (isRefreshTokenError(err)) {
          clearStaleSupabaseAuth();
        } else {
          console.error('Error obteniendo sesión:', err);
        }
        setUser(null);
        setProfile(null);
        setLoading(false);
      });

    // 2. Escuchar cambios en el auth (login/logout)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Si Supabase emite SIGNED_OUT con un refresh token ya inválido, limpiamos
      // por las dudas para que no quede basura entre tabs.
      if (event === 'SIGNED_OUT') {
        clearStaleSupabaseAuth();
      }

      setUser((current) => {
        const nextUser = session?.user ?? null;
        if (current?.id === nextUser?.id) return current;
        return nextUser;
      });
      if (!session?.user) {
        setProfile(null);
        setLoading(false);
        return;
      }

      if (event === 'SIGNED_IN') {
        const currentProfileId = profileRef.current?.id || null;
        if (!currentProfileId || currentProfileId !== session.user.id) {
          fetchProfile(session.user.id);
          return;
        }

        setLoading(false);
        return;
      }

      if (PROFILE_REFRESH_EVENTS.has(event)) {
        fetchProfile(session.user.id);
        return;
      }

      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Función para traer el organization_id y el rol desde nuestra tabla 'profiles'
  const fetchProfile = async (userId) => {
    // Si ya hay un fetch en curso para el mismo usuario, no lo repetimos
    // (ref IMPULZA-4): el primero setea profile + organization para todos.
    if (inFlightProfileUserRef.current === userId) return;
    inFlightProfileUserRef.current = userId;
    try {
    let data = null;
    let embeddedOrganization = null;

    for (let attempt = 0; attempt <= PROFILE_FETCH_RETRY_MS.length; attempt += 1) {
      const { data: nextProfile, error } = await supabase
        .from('profiles')
        .select(profileEmbedSupported ? '*, organizations(*)' : '*')
        .eq('id', userId)
        .maybeSingle();

      // El backend no declara la FK: desactivamos el embed y reintentamos ya
      // mismo sin consumir un intento (no es un profile faltante, es esquema).
      if (error && profileEmbedSupported && isMissingRelationshipError(error)) {
        profileEmbedSupported = false;
        attempt -= 1;
        continue;
      }

      if (nextProfile) {
        // `organizations` es la row embebida, no una columna del profile: la
        // sacamos para que la shape de `profile` siga siendo exactamente la de
        // antes (hay comparacion por campos mas abajo y consumidores en todo
        // el ERP).
        const { organizations: embedded, ...profileRow } = nextProfile;
        data = profileRow;
        embeddedOrganization = embedded || null;
        break;
      }

      if (error) {
        console.error('No pudimos cargar el perfil del usuario.', error);
        break;
      }

      const retryMs = PROFILE_FETCH_RETRY_MS[attempt];
      if (!retryMs) break;
      await new Promise((resolve) => setTimeout(resolve, retryMs));
    }

    setProfile((current) => {
      if (!current || !data) return data;
      if (
        current.id === data.id &&
        current.organization_id === data.organization_id &&
        current.role === data.role &&
        current.full_name === data.full_name &&
        current.email === data.email &&
        current.user_id === data.user_id &&
        JSON.stringify(current.allowed_site_ids || []) === JSON.stringify(data.allowed_site_ids || [])
      ) {
        return current;
      }
      return data;
    });

    // Identificar al usuario en Sentry — todos los errores capturados desde
    // ahora incluyen quién los disparó y a qué organización pertenece.
    if (data) {
      Sentry.setUser({
        id: data.id,
        email: data.email,
        username: data.full_name,
      });
      Sentry.setTag('organization_id', data.organization_id || null);
      Sentry.setTag('role', data.role || null);
    } else {
      Sentry.setUser(null);
    }

    // Cargar la organización del profile si cambió (o no estaba en memoria).
    // Esto evita que cada página haga su propio fetch de la misma row.
    const nextOrgId = data?.organization_id || null;
    if (nextOrgId !== organizationIdRef.current) {
      if (nextOrgId && embeddedOrganization?.id === nextOrgId) {
        // Vino en el mismo request que el profile: nos ahorramos el roundtrip.
        setOrganization(embeddedOrganization);
      } else {
        // Sin embed (esquema sin FK) o el RLS de organizations no dejo leer la
        // row embebida: camino clasico, que ademas loguea el error si falla.
        await fetchOrganization(nextOrgId);
      }
    }

    setLoading(false);
    } finally {
      inFlightProfileUserRef.current = null;
    }
  };

  const refreshProfile = async () => {
    if (!user?.id) return;
    await fetchProfile(user.id);
  };

  const refreshOrganization = async () => {
    const orgId = profileRef.current?.organization_id || null;
    await fetchOrganization(orgId);
  };

  const signOut = async () => {
    // Bitácora de sesiones (super admin): registramos el cierre ANTES de cerrar
    // la sesión, mientras el RLS todavía reconoce al usuario. Best-effort.
    await logAuthEvent('logout', {
      user,
      profile: profileRef.current,
      organization,
    });

    // Avisos push: este equipo deja de recibir los pedidos de la empresa. Si no,
    // el próximo usuario que use el mismo navegador los seguiría recibiendo.
    // Tiene que ir antes del signOut (la baja usa la sesión). Nunca tira.
    await desactivarAvisosAdminEsteDispositivo();

    const { error } = await supabase.auth.signOut();
    const isMissingSessionError = /auth session missing/i.test(String(error?.message || ''));
    if (error && !isMissingSessionError) throw error;

    // Aceleramos la salida visual incluso si el listener tarda un tick.
    setUser(null);
    setProfile(null);
    setOrganization(null);
    setLoading(false);
    Sentry.setUser(null);
  };

  // No bloqueamos el render mientras se resuelve getSession(): las paginas
  // publicas (Login, Landing, Tienda, Rastreo, etc.) NO usan useAuth y
  // pueden mostrarse de inmediato. Las paginas protegidas pasan por
  // ProtectedRoute (src/App.jsx), que tiene su propio gate basado en
  // useAuth().loading. Antes este {!loading && children} dejaba el body
  // en blanco varios segundos en mobile, justo el sintoma que reporto el
  // usuario al entrar a /login.
  return (
    <AuthContext.Provider value={{ user, profile, organization, loading, signOut, refreshProfile, refreshOrganization }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
